import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PhoneCall, X } from 'lucide-react';
import { CALL_LANGUAGES, CALL_OUTCOMES, CALL_STATUSES } from '@fenix/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/StatusBadge';
import { DataTablePagination } from '@/components/DataTablePagination';
import { EmptyState } from '@/components/EmptyState';
import { callsApi, productsApi } from '@/lib/api/endpoints';
import {
  CALL_STATUS_LABEL,
  CALL_STATUS_VARIANT,
  OUTCOME_LABEL,
  fmtDate,
  fmtDateTime,
  fmtDuration,
  fmtPhone,
  fmtUsd,
  sortByName,
} from '@/lib/format';

const ALL = '__all__';

/**
 * As datas ficam no URL como YYYY-MM-DD (legíveis e compartilháveis). Para a API viram ISO
 * cobrindo o dia inteiro no fuso do navegador — "até 08/09" inclui as 23:59 de 08/09.
 */
const dayStart = (d: string) => new Date(`${d}T00:00:00`).toISOString();
const dayEnd = (d: string) => new Date(`${d}T23:59:59.999`).toISOString();
const localDay = (date: Date) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
const daysAgo = (n: number) => localDay(new Date(Date.now() - n * 86_400_000));

const PRESETS = [
  { label: 'Hoje', days: 0 },
  { label: '7 dias', days: 6 },
  { label: '30 dias', days: 29 },
];

const fmtLongDuration = (sec: number | undefined) => {
  if (sec == null) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h ? `${h}h ${m}min` : m ? `${m}min` : fmtDuration(sec);
};

function Stat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

export function CallsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const fromDay = params.get('from') ?? undefined;
  const toDay = params.get('to') ?? undefined;
  const filter = {
    status: params.get('status') ?? undefined,
    outcome: params.get('outcome') ?? undefined,
    campaignId: params.get('campaignId') ?? undefined,
    productCodename: params.get('product') ?? undefined,
    language: params.get('language') ?? undefined,
    search: params.get('search') ?? undefined,
    from: fromDay ? dayStart(fromDay) : undefined,
    to: toDay ? dayEnd(toDay) : undefined,
  };
  const query = { page: Number(params.get('page') ?? 1), pageSize: 25, ...filter };
  const hasFilters = [...params.keys()].some((k) => k !== 'page');

  const set = (k: string, v: string | number | undefined) => {
    const next = new URLSearchParams(params);
    if (v === undefined || v === '' || v === ALL) next.delete(k);
    else next.set(k, String(v));
    if (k !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };
  const setRange = (from?: string, to?: string) => {
    const next = new URLSearchParams(params);
    if (from) next.set('from', from);
    else next.delete('from');
    if (to) next.set('to', to);
    else next.delete('to');
    next.delete('page');
    setParams(next, { replace: true });
  };

  const q = useQuery({
    queryKey: ['calls', query],
    queryFn: () => callsApi.list(query),
    placeholderData: (p) => p,
    refetchInterval: 15_000,
  });
  const summary = useQuery({
    queryKey: ['calls', 'summary', filter],
    queryFn: () => callsApi.summary(filter),
    placeholderData: (p) => p,
    refetchInterval: 15_000,
  });
  const products = useQuery({
    queryKey: ['products', 'all'],
    queryFn: () => productsApi.list(true),
  });

  const s = summary.data;
  const recovered = s?.byOutcome.recovered ?? 0;
  const periodLabel =
    fromDay || toDay
      ? `${fromDay ? fmtDate(dayStart(fromDay)) : '…'} – ${toDay ? fmtDate(dayStart(toDay)) : '…'}`
      : 'Todo o período';
  const outcomeChips = CALL_OUTCOMES.filter((o) => s?.byOutcome[o]).map(
    (o) => `${OUTCOME_LABEL[o]} ${s!.byOutcome[o]}`,
  );

  return (
    <div>
      <PageHeader
        title="Chamadas"
        description="Histórico completo com transcrição, gravação e resultado"
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          className="w-56"
          placeholder="Nome ou telefone"
          defaultValue={filter.search ?? ''}
          onKeyDown={(e) =>
            e.key === 'Enter' && set('search', (e.target as HTMLInputElement).value)
          }
          onBlur={(e) => e.target.value !== (filter.search ?? '') && set('search', e.target.value)}
        />
        <Select value={filter.status ?? ALL} onValueChange={(v) => set('status', v)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os status</SelectItem>
            {CALL_STATUSES.map((st) => (
              <SelectItem key={st} value={st}>
                {CALL_STATUS_LABEL[st]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filter.outcome ?? ALL} onValueChange={(v) => set('outcome', v)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Resultado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os resultados</SelectItem>
            {CALL_OUTCOMES.map((o) => (
              <SelectItem key={o} value={o}>
                {OUTCOME_LABEL[o]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filter.productCodename ?? ALL} onValueChange={(v) => set('product', v)}>
          <SelectTrigger className="w-52">
            <SelectValue placeholder="Produto" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os produtos</SelectItem>
            {sortByName(products.data ?? []).map((p) => (
              <SelectItem key={p.codename} value={p.codename}>
                {p.name}
                {!p.active && ' (inativo)'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filter.language ?? ALL} onValueChange={(v) => set('language', v)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Idioma" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os idiomas</SelectItem>
            {CALL_LANGUAGES.map((l) => (
              <SelectItem key={l.code} value={l.code}>
                {l.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Período</span>
        <Input
          type="date"
          className="w-40"
          value={fromDay ?? ''}
          max={toDay}
          onChange={(e) => setRange(e.target.value || undefined, toDay)}
        />
        <span className="text-sm text-muted-foreground">até</span>
        <Input
          type="date"
          className="w-40"
          value={toDay ?? ''}
          min={fromDay}
          onChange={(e) => setRange(fromDay, e.target.value || undefined)}
        />
        {PRESETS.map((p) => {
          const active = fromDay === daysAgo(p.days) && toDay === daysAgo(0);
          return (
            <Button
              key={p.label}
              size="sm"
              variant={active ? 'secondary' : 'outline'}
              onClick={() => setRange(daysAgo(p.days), daysAgo(0))}
            >
              {p.label}
            </Button>
          );
        })}
        {hasFilters && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setParams(new URLSearchParams(), { replace: true })}
          >
            <X /> Limpar filtros
          </Button>
        )}
      </div>

      <div className="mb-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label={periodLabel} value={s?.total ?? '—'} hint="chamadas" />
        <Stat
          label="Recuperadas"
          value={recovered}
          hint={s?.total ? `${Math.round((recovered / s.total) * 100)}% do período` : undefined}
        />
        <Stat label="Duração total" value={fmtLongDuration(s?.durationSec)} />
        <Stat label="Custo" value={fmtUsd(s?.costUsd)} />
      </div>
      <p className="mb-4 min-h-4 text-xs text-muted-foreground">
        {outcomeChips.length ? `Por resultado: ${outcomeChips.join(' · ')}` : ''}
      </p>

      <div className="rounded-lg border bg-card">
        {q.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : q.data?.items.length ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Lead</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Resultado</TableHead>
                  <TableHead>Duração</TableHead>
                  <TableHead>Custo</TableHead>
                  <TableHead>Idioma</TableHead>
                  <TableHead>Provedor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.data.items.map((c) => (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/chamadas/${c.id}`)}
                  >
                    <TableCell className="text-sm">{fmtDateTime(c.createdAt)}</TableCell>
                    <TableCell>
                      <p className="font-medium">{c.lead?.name || 'Sem nome'}</p>
                      <p className="text-xs text-muted-foreground tabular-nums">
                        {fmtPhone(c.toNumber)}
                      </p>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {c.lead?.productCodename ?? '—'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        label={CALL_STATUS_LABEL[c.status]}
                        className={CALL_STATUS_VARIANT[c.status]}
                      />
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.outcome ? OUTCOME_LABEL[c.outcome] : '—'}
                    </TableCell>
                    <TableCell className="tabular-nums">{fmtDuration(c.durationSec)}</TableCell>
                    <TableCell className="tabular-nums">{fmtUsd(c.costUsd)}</TableCell>
                    <TableCell className="text-sm">{c.language}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.provider}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <DataTablePagination
              page={q.data.page}
              pageSize={q.data.pageSize}
              total={q.data.total}
              onPageChange={(p) => set('page', p)}
            />
          </>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={PhoneCall}
              title={hasFilters ? 'Nenhuma chamada com esses filtros' : 'Nenhuma chamada'}
              description={
                hasFilters
                  ? 'Amplie o período ou limpe os filtros.'
                  : 'As ligações feitas pela IA aparecerão aqui com transcrição e resultado.'
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
